<template>
  <v-container
    class="container rounded elevation-3"
    fluid>
    <v-form
      ref="workflowForm"
      v-model="formValid"
      tag="form"
      :loading="loading"
      @submit.prevent="saveWorkflow()">
      <v-row
        no-gutters
        justify="space-between"
        align="center"
        class="mb-4"
      >
        <v-col
          cols="12"
          class="d-flex">
          <h2>Workflow Setup</h2>
          <v-spacer></v-spacer>
          <v-btn
            color="secondary"
            outlined
            class="mr-4"
            @click="addTimeline"
          >
            <v-icon left>
              mdi-timeline
            </v-icon>
            เพิ่ม Timeline
          </v-btn>
          <v-btn
            color="secondary"
            outlined
            :loading="loading"
            class="mr-8"
            @click="addStage"
          >
            <v-icon left>
              mdi-table-column-plus-after
            </v-icon>
            เพิ่ม STAGE
          </v-btn>
          <v-btn
            color="secondary"
            outlined
            class="mr-4"
            @click="importDialog = true"
          >
            <v-icon left>
              mdi-upload
            </v-icon>
            Import
          </v-btn>
          <v-btn
            color="secondary"
            outlined
            class="mr-4"
            @click="exportWorkflow"
          >
            <v-icon left>
              mdi-download
            </v-icon>
            Export
          </v-btn>
          <v-btn
            color="secondary"
            :loading="loading"
            :disabled="!canSave || loading"
            type="submit"
            width="140"
          >
            <v-icon left>
              mdi-content-save
            </v-icon>
            Save
          </v-btn>
        </v-col>
      </v-row>

      <v-row no-gutters>
        <v-col
          cols="12"
          class="d-flex">
          <div
            ref="timelineScroller"
            class="d-flex flex-wrap"
            style="height: calc(100vh - 145px); overflow-y: auto; width: 100%;"
          >
            <div
              v-if="loading"
              class="tableContainer d-flex white justify-center align-center"
            >
              <v-progress-circular
                :size="50"
                color="primary"
                indeterminate
              ></v-progress-circular>
            </div>

            <!-- timelines 20% -->
            <div style="width: 20%; position: relative; z-index: 1; box-shadow: 2px 0 4px rgba(0, 0, 0, 0.15);">
              <div
                class="mt-12"
                style="height: 235px;">
              </div>
              <div
                v-for="(timeline, timelineIndex) in workflow.timelines.filter((s) => s.status !== 'inactive')"
                :key="`timeline-${timelineIndex}`"
              >
                <div class="d-flex mr-2">
                  <div>
                    <v-btn
                      color="error"
                      small
                      icon
                      class="mr-2"
                      :loading="loading"
                      :disabled="!canRemoveTimeline"
                      @click="removeTimeline(timeline)">
                      <v-icon>
                        mdi-delete
                      </v-icon>
                    </v-btn>
                  </div>
                  <v-text-field
                    ref="timelineNames"
                    v-model="timeline.name"
                    outlined
                    label="Timeline"
                    :rules="[
                      (v) => !!v || 'กรุณาระบุ Timeline',
                      v => !isDuplicateName(v, timeline, 'timelines') || 'Timeline ซ้ำ'
                    ]"
                    dense
                    style="margin-bottom: 10px; min-height: 90px"
                    @input="revalidateTimelineNames"
                  />
                </div>
              </div>
            </div>
            <div
              class="d-flex"
              style="overflow-x: scroll; width: 80%; padding-left: 20px;">
              <!-- stages -->
              <draggable
                ref="stageScroller"
                class="d-flex flex-nowrap draggable-box"
                :list="activeStages"
                @end="onStageDragEnd"
              >
                <v-card
                  v-for="(stage, stageIndex) in activeStages"
                  :key="`stage-${stageIndex}`"
                  class="pa-1 flex-shrink-0"
                  elevation="0"
                  style="min-width: 308px;">
                  <div class="stages-card pa-3 cursor-pointer">
                    <div
                      class="mb-4"
                      style="height: 250px;"
                    >
                      <div class="d-flex justify-center">
                        <v-btn
                          color="error"
                          small
                          icon
                          style="margin-bottom: 10px;"
                          :loading="loading"
                          :disabled="!canRemoveStage"
                          @click="removeStage(stage)">
                          <v-icon>
                            mdi-delete
                          </v-icon>
                        </v-btn>
                      </div>
                      <v-text-field
                        ref="stageNames"
                        v-model="stage.name"
                        label="Stage"
                        outlined
                        :rules="[
                          (v) => !!v || 'กรุณาระบุ Stage',
                          v => !isDuplicateName(v, stage, 'stages') || 'Stage ซ้ำ'
                        ]"
                        dense
                        style="margin-bottom: 10px;"
                        @input="revalidateStageNames"
                      />
                      <v-select
                        v-model="stage.pic"
                        :items="jobs"
                        item-text="name"
                        item-value="value"
                        placeholder="โปรดเลือก Job"
                        :rules="[
                          (v) => !!v || 'กรุณาระบุ Job'
                        ]"
                        label="Job"
                        outlined
                        dense
                      />
                      <div class="d-flex">
                        <v-checkbox
                          v-model="stage.isShowInFactoryTimeline"
                          class="ma-0 pa-0 pt-2"
                          hide-details
                          @change="onFactoryTimelineChange(stage)"
                        ></v-checkbox>
                        <v-text-field
                          ref="stageNamesCn"
                          v-model="stage.factoryTimelineHeader"
                          :disabled="!stage.isShowInFactoryTimeline"
                          label="Stage (CN)"
                          outlined
                          dense
                          :hide-details="!stage.isShowInFactoryTimeline"
                          :rules="stage.isShowInFactoryTimeline ? [
                            (v) => !!v || 'กรุณาระบุ Stage (CN)',
                            (v) => !isDuplicateFactoryHeader(v, stage) || 'Stage (CN) ซ้ำ'
                          ] : []"
                          @input="revalidateStageNamesCn"
                        />
                      </div>
                    </div>

                    <div
                      v-for="(timeline, timelineIndex) in workflow.timelines.filter((s) => s.status !== 'inactive')"
                      :key="`timeline-${timelineIndex}`"
                      class="d-flex flex-row">
                      <!-- Start Date — reactive via :error-messages -->
                      <v-text-field
                        v-model="getTimelineStage(timeline, stage.id).start"
                        label="Start Date"
                        placeholder="Start Date"
                        type="number"
                        inputmode="numeric"
                        :error-messages="getStartDateError(timeline, stage.id)"
                        outlined
                        dense
                        style="margin-bottom: 10px; margin-right: 2%; max-width: 49%; min-height: 90px"
                        onwheel="this.blur()"
                        @wheel="$event.target.blur()"
                      />
                      <!-- Due Date — reactive via :error-messages -->
                      <v-text-field
                        v-model="getTimelineStage(timeline, stage.id).due"
                        label="Due Date"
                        placeholder="Due Date"
                        type="number"
                        inputmode="numeric"
                        :error-messages="getDueDateError(timeline, stage.id)"
                        outlined
                        dense
                        style="margin-bottom: 10px; max-width: 49%; min-height: 90px"
                        onwheel="this.blur()"
                        @wheel="$event.target.blur()"
                      />
                    </div>
                  </div>
                </v-card>
              </draggable>

              <!-- launch field -->
              <v-card
                class="px-1 flex-shrink-0"
                elevation="0"
                style="min-width: 159px;">
                <div class="px-3 text-center">
                  <div
                    class="mt-12"
                    style="height: 235px">
                    <h3
                      class="mb-0 font-weight-regular"
                      style="padding-top: 90px;">
                      LAUNCH
                    </h3>
                  </div>
                  <div
                    v-for="(timeline, timelineIndex) in workflow.timelines.filter((s) => s.status !== 'inactive')"
                    :key="`timeline-${timelineIndex}`"
                  >
                    <div class="d-flex">
                      <!-- Launch — reactive via :error-messages -->
                      <v-text-field
                        v-model="timeline['launch']"
                        outlined
                        label="Launch"
                        type="number"
                        inputmode="numeric"
                        :error-messages="getLaunchError(timeline)"
                        dense
                        style="margin-bottom: 10px; min-height: 90px; max-width: 135px;"
                        onwheel="this.blur()"
                        @wheel="$event.target.blur()"
                      />
                    </div>
                  </div>
                </div>
              </v-card>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-form>

    <workflow-import
      v-if="importDialog"
      :active-stages="activeStages"
      :jobs="jobs"
      :existing-timelines="activeTimelines"
      @imported="applyImport"
      @close="importDialog = false"
    />
  </v-container>
</template>

<script>
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'
import { mapActions } from 'vuex/dist/vuex.common'
import Draggable from 'vuedraggable'
import { ExportToCsv } from 'export-to-csv'
import WorkflowImport from '../components/WorkflowImport.vue'

const ProductionPlanService = new ProductionPlanProvider()

export default {
  components: {
    Draggable,
    WorkflowImport
  },
  data () {
    return {
      jobs: [
        { name: 'Designer', value: 'designer' },
        { name: 'Merchandiser', value: 'merchandiser' },
        { name: 'Graphic', value: 'graphic' },
        { name: 'Creative / Stylist', value: 'creative' },
        { name: 'Visual Merchandiser', value: 'visual_merchandiser' },
        { name: 'Marketing', value: 'marketing' },
        { name: 'Producer', value: 'producer' }
      ],
      workflow: {
        name: '',
        stages: [],
        timelines: []
      },
      loading: false,
      formValid: false,
      originalWorkflow: null,
      stageRemove: [],
      importDialog: false
    }
  },
  computed: {
    activeStages () {
      return this.workflow.stages
        .filter((s) => s.status !== 'inactive')
        .sort((a, b) => a.order - b.order)
    },
    activeTimelines () {
      return this.workflow.timelines.filter((s) => s.status !== 'inactive')
    },
    canRemoveStage () {
      return this.activeStages.length > 1
    },
    canRemoveTimeline () {
      return this.activeTimelines.length > 1
    },
    isEdit () {
      if (!this.originalWorkflow) return false
      return JSON.stringify(this.workflow) !== JSON.stringify(this.originalWorkflow)
    },
    // ปุ่ม Save กดได้เมื่อ formValid และไม่มี error จาก error-message methods
    canSave () {
      if (!this.formValid) return false
      return !this.hasDateErrors
    },
    hasDateErrors () {
      return this.activeTimelines.some((tl) => {
        if (this.getLaunchError(tl)) return true
        return this.activeStages.some((stage) => (
            this.getStartDateError(tl, stage.id)
            || this.getDueDateError(tl, stage.id)
        ))
      })
    }
  },
  mounted () {
    this.fetchWorkflow()
  },
  beforeRouteLeave (to, from, next) {
    if (!this.isEdit) {
      next()
      return
    }
    // eslint-disable-next-line no-alert
    const leave = window.confirm('You have unsaved changes.\nDo you want to stay and continue editing, or leave without saving?')
    if (leave) {
      next()
    } else {
      next(false)
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),
    isDuplicateName (name, currentItem, type) {
      if (!name) return false
      return this.workflow[type]
        .filter((s) => s.status !== 'inactive')
        .some(
          (s) => s !== currentItem
            && s.name
            && s.name.trim().toLowerCase() === name.trim().toLowerCase()
        )
    },
    getStartDateError (timeline, stageId) {
      const v = this.getTimelineStage(timeline, stageId).start
      if (v === null || v === '') return 'กรุณาระบุ Start Date'
      if (!/^[0-9]+$/.test(String(v)) || Number(v) === 0) return 'กรอกได้เฉพาะตัวเลขจำนวนเต็มบวก'
      return ''
    },

    getDueDateError (timeline, stageId) {
      // eslint-disable-next-line prefer-destructuring
      const ts = this.getTimelineStage(timeline, stageId)
      const v = ts.due
      // eslint-disable-next-line prefer-destructuring
      const start = ts.start
      if (v === null || v === '') return 'กรุณาระบุ Due Date'
      if (!/^[0-9]+$/.test(String(v)) || Number(v) === 0) return 'กรอกได้เฉพาะตัวเลขจำนวนเต็มบวก'
      if (
        start !== '' && start !== null && start !== undefined && Number(v) < Number(start)
      ) return 'Due Date ต้องมากกว่าหรือเท่ากับ Start Date'
      return ''
    },

    getLaunchError (timeline) {
      const v = timeline.launch
      if (!v && v !== 0) return 'กรุณาระบุ Launch'
      if (!/^[0-9]+$/.test(String(v)) || Number(v) === 0) return 'กรอกได้เฉพาะตัวเลขจำนวนเต็มบวก'
      const launchNum = Number(v)
      for (const s of timeline.stages) {
        const start = Number(s.start)
        const due = Number(s.due)
        if (s.start !== '' && s.start !== null && !Number.isNaN(start) && launchNum < start) {
          return 'Launch ต้องมากกว่าหรือเท่ากับ ค่าที่มากที่สุดใน Timeline'
        }
        if (s.due !== '' && s.due !== null && !Number.isNaN(due) && launchNum < due) {
          return 'Launch ต้องมากกว่าหรือเท่ากับ ค่าที่มากที่สุดใน Timeline'
        }
      }
      return ''
    },
    initDefaultWorkflow () {
      const mockupStageId = `temp-${Date.now()}`
      if (this.workflow.stages.length === 0) {
        this.workflow.stages.push({
          id: mockupStageId,
          name: '',
          pic: null,
          order: 1,
          status: 'active'
        })
      }
      if (this.workflow.timelines.length === 0) {
        this.workflow.timelines.push({
          name: '',
          status: 'active',
          stages: this.workflow.stages.map(() => ({
            stageId: mockupStageId,
            start: '',
            due: ''
          }))
        })
      }
    },
    getTimelineStage (timeline, stageId) {
      let stage = timeline.stages.find((s) => s.stageId === stageId)
      if (!stage) {
        stage = { stageId, start: '', due: '' }
        timeline.stages.push(stage)
      }
      return stage
    },
    async fetchWorkflow () {
      try {
        this.loading = true
        const { data } = await ProductionPlanService.getWorkflow()
        if (data) {
          data.timelines.forEach((timeline) => {
            // eslint-disable-next-line no-param-reassign
            timeline.stages = timeline.stages
              .filter((s) => s.id || s.stageId)
              .map((s) => ({
                stageId: s.stageId || s.id,
                start: s.start,
                due: s.due
              }))
          })
          this.workflow = data
          this.workflow.stages = data.stages.sort((a, b) => a.order - b.order)
          this.initDefaultWorkflow()
        } else {
          this.initDefaultWorkflow()
          await ProductionPlanService.createWorkflow({ stages: [], timelines: [] })
          this.fetchWorkflow()
        }
        this.originalWorkflow = JSON.parse(JSON.stringify(this.workflow))
      } catch (error) {
        console.error('fetchWorkflow :', error)
        this.setErrorPage({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async addTimeline () {
      const stages = this.workflow.stages.map((stage) => ({
        stageId: stage.id,
        start: '',
        due: ''
      }))
      this.workflow.timelines.push({ name: '', stages })
      await this.$nextTick()
      this.scrollTimelineToBottom()
    },
    async addStage () {
      const mockupStageId = `temp-${Date.now()}`
      this.workflow.stages.push({
        id: mockupStageId,
        name: '',
        pic: null,
        order: this.workflow.stages.length + 1,
        status: 'active'
      })
      this.workflow.timelines.forEach((timeline) => {
        timeline.stages.push({ stageId: mockupStageId, start: '', due: '' })
      })
      await this.$nextTick()
      this.scrollStageToRight()
    },
    scrollStageToRight () {
      const el = this.$refs.stageScroller?.$el || this.$refs.stageScroller
      if (!el) return
      el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' })
    },
    scrollTimelineToBottom () {
      const el = this.$refs.timelineScroller
      if (!el) return
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    },
    isTempId (id) {
      return !id || String(id).startsWith('temp-')
    },
    async saveWorkflow () {
      try {
        // validate fields ที่ยังใช้ rules (stage name, timeline name, job)
        const formValid = await this.$refs.workflowForm.validate()
        if (!formValid || this.hasDateErrors) return

        this.loading = true
        const payload = {
          name: this.workflow.name,
          stages: this.workflow.stages.map((stage) => ({
            _id: this.isTempId(stage.id) ? undefined : stage.id,
            tempId: this.isTempId(stage.id) ? stage.id : undefined,
            name: stage.name,
            order: stage.order,
            pic: stage.pic,
            status: stage.status,
            isShowInFactoryTimeline: stage.isShowInFactoryTimeline || false,
            factoryTimelineHeader: stage.factoryTimelineHeader || ''
          })),
          timelines: this.workflow.timelines.map((tl) => ({
            _id: this.isTempId(tl.id) ? undefined : tl.id,
            name: tl.name,
            launch: Number(tl.launch),
            stages: tl.stages.map((s) => ({
              _id: s.stageId,
              start: Number(s.start),
              due: Number(s.due)
            })),
            status: tl.status
          }))
        }

        const { data } = await ProductionPlanService.updateWorkflow(this.workflow.id, payload)
        if (data) {
          this.setSnackbar({
            value: true,
            type: 'success',
            message: 'Workflow saved successfully'
          })
          if (this.stageRemove.length > 0) {
            await ProductionPlanService.removeStage(this.stageRemove)
          }
          this.stageRemove = []
          this.originalWorkflow = JSON.parse(JSON.stringify(this.workflow))
          this.fetchWorkflow()
        }
      } catch (error) {
        console.error('saveWorkflow :', error)
        this.setSnackbar({
          value: true,
          color: 'error',
          message: `Error: ${error.message}`
        })
      } finally {
        this.loading = false
      }
    },
    removeTimeline (timeline) {
      this.setModal({
        value: true,
        title: 'Remove Timeline',
        message: 'Do you want to remove the Timeline',
        confirmText: 'Remove',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => {
          if (timeline.status) {
            // eslint-disable-next-line no-param-reassign
            timeline.status = 'inactive'
          } else {
            this.workflow.timelines = this.workflow.timelines.filter((s) => s !== timeline)
          }
          this.revalidateTimelineNames()
        }
      })
    },
    removeStage (stage) {
      this.setModal({
        value: true,
        title: 'Remove Stage',
        message: 'Do you want to remove the Stage',
        confirmText: 'Remove',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => {
          const isTempId = stage.id.startsWith('temp-')
          if (stage.status && stage.name !== '' && !isTempId) {
            // eslint-disable-next-line no-param-reassign
            stage.status = 'inactive'
            this.stageRemove.push(stage.id)
          } else {
            this.workflow.stages = this.workflow.stages.filter((s) => s !== stage)
          }
          this.revalidateStageNames()
          this.workflow.timelines.forEach((timeline) => {
            // eslint-disable-next-line no-param-reassign
            timeline.stages = timeline.stages.filter((ts) => ts.stageId !== stage.id)
          })
        }
      })
    },

    onStageDragEnd () {
      this.activeStages.forEach((stage, index) => {
        // eslint-disable-next-line no-param-reassign
        stage.order = index + 1
      })
    },
    revalidateStageNames () {
      this.$nextTick(() => {
        const refs = this.$refs.stageNames
        if (Array.isArray(refs)) refs.forEach((ref) => ref && ref.validate())
      })
    },
    revalidateTimelineNames () {
      this.$nextTick(() => {
        const refs = this.$refs.timelineNames
        if (Array.isArray(refs)) refs.forEach((ref) => ref && ref.validate())
      })
    },
    onFactoryTimelineChange (stage) {
      if (!stage.isShowInFactoryTimeline) {
        // eslint-disable-next-line no-param-reassign
        stage.factoryTimelineHeader = ''
      }
      this.revalidateStageNamesCn()
    },
    revalidateStageNamesCn () {
      this.$nextTick(() => {
        const refs = this.$refs.stageNamesCn
        if (Array.isArray(refs)) refs.forEach((ref) => ref && ref.validate())
      })
    },
    async applyImport (result) {
      try {
        this.loading = true
        let added = 0
        let updated = 0

        for (const { timelineName, changes, launch } of result) {
          let timeline = this.workflow.timelines.find(
            (t) => t.status === 'active'
              && t.name?.trim().toLowerCase() === timelineName?.trim().toLowerCase()
          )

          if (!timeline) {
            const stages = this.workflow.stages.map((stage) => ({
              stageId: stage.id,
              start: '',
              due: ''
            }))
            timeline = { name: timelineName, stages, status: 'active', launch: '' }
            this.workflow.timelines.push(timeline)
            added += 1
          } else {
            updated += 1
          }

          if (launch !== undefined && launch !== null && launch !== '') {
            timeline.launch = launch
          }

          for (const { stageId, field, value } of changes) {
            const ts = this.getTimelineStage(timeline, stageId)
            ts[field] = value
          }
        }

        const payload = {
          name: this.workflow.name,
          stages: this.workflow.stages.map((stage) => ({
            _id: this.isTempId(stage.id) ? undefined : stage.id,
            tempId: this.isTempId(stage.id) ? stage.id : undefined,
            name: stage.name,
            order: stage.order,
            pic: stage.pic,
            status: stage.status,
            isShowInFactoryTimeline: stage.isShowInFactoryTimeline || false,
            factoryTimelineHeader: stage.factoryTimelineHeader || ''
          })),
          timelines: this.workflow.timelines.map((tl) => ({
            _id: this.isTempId(tl.id) ? undefined : tl.id,
            name: tl.name,
            launch: Number(tl.launch || 0),
            stages: tl.stages.map((s) => ({
              _id: s.stageId,
              start: Number(s.start || 0),
              due: Number(s.due || 0)
            })),
            status: tl.status
          }))
        }

        const { data } = await ProductionPlanService.updateWorkflow(this.workflow.id, payload)
        if (data) {
          this.setSnackbar({
            value: true,
            type: 'success',
            message: `Import สำเร็จ: เพิ่ม ${added} timeline, อัปเดต ${updated} timeline`
          })
          this.stageRemove = []
          this.originalWorkflow = JSON.parse(JSON.stringify(this.workflow))
          this.fetchWorkflow()
        }
      } catch (error) {
        console.error('applyImport :', error)
        this.setSnackbar({
          value: true,
          color: 'error',
          message: `Import ล้มเหลว: ${error.message}`
        })
      } finally {
        this.loading = false
      }
    },
    exportWorkflow () {
      const stages = this.activeStages
      const timelines = this.activeTimelines

      const data = timelines.map((timeline) => {
        const row = { Timeline: timeline.name }
        for (const stage of stages) {
          const ts = this.getTimelineStage(timeline, stage.id)
          const job = this.jobs.find((j) => j.value === stage.pic)
          const jobLabel = job ? job.name : stage.pic || ''
          row[`${stage.name} (${jobLabel}) Start`] = ts.start !== '' && ts.start !== null ? ts.start : ''
          row[`${stage.name} (${jobLabel}) Due`] = ts.due !== '' && ts.due !== null ? ts.due : ''
        }
        row['Launch'] = timeline.launch !== '' && timeline.launch !== null ? timeline.launch : ''
        return row
      })

      const exporter = new ExportToCsv({
        filename: 'workflow-setup',
        showLabels: true,
        useKeysAsHeaders: true
      })
      exporter.generateCsv(data)
    },
    isDuplicateFactoryHeader (header, currentStage) {
      if (!header) return false
      return this.workflow.stages
        .filter((s) => s.status !== 'inactive' && s.isShowInFactoryTimeline)
        .some(
          (s) => s !== currentStage
            && s.factoryTimelineHeader
            && s.factoryTimelineHeader.trim().toLowerCase() === header.trim().toLowerCase()
        )
    }
  }
}
</script>

<style scoped>
.add-stage-btn {
  display: flex;
  justify-content: center;
  align-items: center;
}
.stages-card {
  border: thin dashed #afafaf;
  max-width: 300px;
}
.cursor-pointer {
  cursor: pointer;
}
.tableContainer {
  width: 100%;
  height: 100%;
}
.container {
  background-color: white;
  min-height: calc(100vh - 70px);
}
</style>
